import { Stack } from "@phosphor-icons/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace } from "~/api/queries";
import type { DigestPreview, DigestRequest } from "~/features/analysis/digestion";
import { pageContent } from "./pageContent";
const DeduplicatorDrawer = lazy(() =>
  import("~/features/analysis/DeduplicatorDrawer").then((m) => ({ default: m.DeduplicatorDrawer })),
);
const DigestReadingView = lazy(() =>
  import("~/features/analysis/DigestReading").then((m) => ({ default: m.DigestReadingView })),
);

export function DeduplicatePageButton({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState<Partial<DigestRequest>>({});
  const [preview, setPreview] = useState<DigestPreview>();
  const [reading, setReading] = useState(false);
  const show = () => {
    // Reading surfaces know their native selection and can own the drawer.
    if (!window.dispatchEvent(new CustomEvent("docit:deduplicate", { cancelable: true }))) return;
    const content = pageContent(window.document, 999999);
    const document = /^\/documents\/([^/]+)/.exec(pathname)?.[1];
    const collection = /^\/collections\/([^/]+)/.exec(pathname)?.[1];
    setInitial(
      document
        ? { scope: "items", keys: [`document:${document}`], title: content.title }
        : collection
          ? { scope: "collection", scopeId: collection, title: content.title }
          : {
              scope: "text",
              title: content.title,
              sources: [
                { id: "page", title: content.title ?? t("Current page"), text: content.text },
              ],
            },
    );
    setOpen(true);
  };
  return (
    <>
      <button
        type="button"
        aria-label={t("Deduplicate this page")}
        title={t("Deduplicate this page")}
        onClick={show}
        className="flex h-8 items-center rounded-sm px-2 text-text-secondary hover:bg-hover-bg"
      >
        <Stack size={19} />
      </button>
      {open && (
        <Suspense fallback={<span role="status">{t("Loading…")}</span>}>
          <DeduplicatorDrawer
            open={open}
            onOpenChange={setOpen}
            workspace={workspace.data?.id ?? ""}
            initial={initial}
            onPreview={(value) => {
              setPreview(value);
              setReading(true);
            }}
          />
        </Suspense>
      )}
      {reading && preview && (
        <DigestProjection
          preview={preview}
          title={initial.title}
          onRestore={() => setReading(false)}
        />
      )}
    </>
  );
}
function DigestProjection({
  preview,
  title,
  onRestore,
}: {
  preview: DigestPreview;
  title?: string;
  onRestore: () => void;
}) {
  const { t } = useTranslation();
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const main = document.querySelector<HTMLElement>("main");
    if (!main) return;
    main.classList.add("docit-digest-reading");
    setHost(main);
    return () => main.classList.remove("docit-digest-reading");
  }, []);
  return host
    ? createPortal(
        <article
          data-digest-reading
          className="digest-page-projection"
          aria-label={t("Deduplicated page")}
        >
          <header>
            <h1>{title ?? t("Concise story")}</h1>
            <button type="button" onClick={onRestore}>
              {t("Show original page")}
            </button>
          </header>
          <Suspense fallback={<p>{t("Loading…")}</p>}>
            <DigestReadingView preview={preview} title={title} />
          </Suspense>
        </article>,
        host,
      )
    : null;
}
